import moment from "moment";
import { RootState } from "../root-reducer";
import { FlatItem, ApiFlatItem } from "../../types/general";
import { AllClaims } from "../../types/claim";
import {
  AcePlan,
  AllPlans,
  CorePlan,
  FppPlan,
  LegacyPlan,
  PlanStatus,
  PlanTypes,
} from "../../types/plan";
import { UploadedFile } from "../../types/uploaded-file";
import { WarrantyItem } from "../../types/warranty-item";

const mapCorePlan = (payload: unknown): CorePlan => {
  return {
    id: payload["warr_id"],
    aceMigrated: parseInt(payload["aceMigrated"]),
    planStatus: mapPlanStatus(payload["planStatus"] || payload["PlanStatus"]),
    programName: payload["warr_program_name"],
    dealerId: payload["warr_dealer_id"],
    invoiceId: payload["warr_invoice"],
    number: payload["warr_number"],
    programId: payload["warr_program_id"],
    deliveryDate: payload["warr_delivery_date"]
      ? moment(payload["warr_delivery_date"], "YYYY-MM-DD").toDate()
      : null,
    purchaseDate: payload["warr_purchase_date"]
      ? moment(payload["warr_purchase_date"], "YYYY-MM-DD").toDate()
      : null,
    expirationDate: payload["warr_expiration_date"]
      ? moment(payload["warr_expiration_date"], "YYYY-MM-DD").toDate()
      : null,
    store: payload["warr_stor_loc_name"],
    totalPrice: payload["warr_total_price"],
    warrantyProgramPrice: +payload["warranty_program_price"],
  };
};

export const mapPlanStatus = (status) => {
  const allStatus = {
    Active: PlanStatus.ACTIVE,
    Cancelled: PlanStatus.CANCELLED,
    Expired: PlanStatus.Expired,
    "Cancel Request Dealer Review": PlanStatus.CancelDealerReview,
    "Cancel Request Consumer Review": PlanStatus.CancelCustomerReview,
  };
  return allStatus[status] || null;
};

export const mapApiFppPlan = (payload: unknown): FppPlan => {
  return {
    ...mapCorePlan(payload),
    id: +payload["warr_id"],
    customerName:
      `${payload["cust_fname"] || ""} ${payload["cust_lname"] || ""}`.trim() ||
      "Unknown Customer",
    customerId: payload["cust_id"],
    planType: PlanTypes.FPP,
    warrantyItems: payload["warrantyItems"]?.map(mapApiWarrantyItem) || [],
    internallyGenerated: false,
  };
};

export const mapApiLegacyPlan = (payload: unknown): LegacyPlan => {
  return {
    ...mapApiFppPlan(payload),
    planType: PlanTypes.LEGACY,
  };
};

export const mapApiAcePlan = (payload: unknown): AcePlan => {
  return {
    ...mapCorePlan(payload),
    id: +payload["warr_id"],
    customerName:
      `${payload["cust_fname"] || ""} ${payload["cust_lname"] || ""}`.trim() ||
      "Unknown Customer",
    customerId: payload["cust_id"],
    planType: PlanTypes.ACE,
    warrantyItems: payload["warrantyItems"]?.map(mapApiWarrantyItem) || [],
    internallyGenerated: false,
  };
};

export const mapApiWarrantyItem = (payload: unknown): WarrantyItem => {
  return {
    id: +payload["id"],
    trackKey: payload["trackKey"],
    itemOrigin: payload["itemOrigin"],
    manufacturer: {
      id: payload["manufact"],
      name: payload["manufact_disp"],
    },
    type: {
      id: payload["item_type"],
      name: payload["item_type_disp"],
    },
    material: { id: payload["material"], name: payload["material_disp"] },
    color: { id: payload["color"], name: payload["color_disp"] },
    itemDescription: payload["item_descr"],
    vendor: payload["vendor"],
    sku: payload["sku"],
    itemPrice: payload["item_price"],
    modelNumber: payload["model_num"],
    quantity: payload["quantity"],
    returned: !!payload["returned"],
    full_replacement: !!payload["full_repl"],
  };
};

export const mapApiFlatData = (payload: ApiFlatItem): FlatItem => ({
  id: payload?.id || payload?.ID,
  name: payload.name || payload.NAME,
});

export const getPlans = (state: RootState): AllPlans[] => state.user.plans;
export const getClaims = (state: RootState): AllClaims[] => state.user.claims;

export const getItemType = (fileName) => {
  const ext = fileName.split(".").pop() || "";
  return ext.toLowerCase() === "pdf" ? "pdf" : "3003";
};

export const mapApiUploadedFile = (payload: unknown): UploadedFile => {
  return {
    id: +payload["id"],
    name: payload["doc_name"],
    docDef: payload["docdef"],
    claimId: +payload["ref_id"],
    // type:  payload['type'] : getItemType(payload['doc_name']),
    type: getItemType(payload["doc_name"] || ""),
    addedDate: payload["date_added"] && moment(payload["date_added"]).toDate(),

    //non-obligatory params
    addedBy: payload["added_by"] ?? "CP2.0",
    fileType: payload["file_type"],
    linkKey: payload["linkKey"],
    metaTag1: payload["meta_tag_1"],
    metaTag2: payload["meta_tag_2"],
    notes: payload["notes"],
    tempFlag: payload["temp_flag"],
    ts: payload["ts"] && moment(payload["ts"]).toDate(),
    ...(payload["gbs_claim_items_damage_areas_id"] && {
      cliamItemDamageId: +payload["gbs_claim_items_damage_areas_id"],
    }),
    ...(payload["gbs_claim_items_id"] && {
      claimItemsId: +payload["gbs_claim_items_id"],
    }),
  };
};

//is used once new document is uploaded; payload differs from claim details uploaded file!
export const mapApiInsertedFile = (payload: unknown): UploadedFile => {
  return {
    id: +payload["id"],
    name: payload["name"],
    docDef: payload["docDef"],
    claimId: +payload["claimId"],
    type: payload["type"],
    addedDate: payload["addedDate"] && moment(payload["addedDate"]).toDate(),

    //non-obligatory params
    addedBy: payload["added_by"] ?? "CP2.0",

    ...(payload["claimItemsDamageAreasId"] && {
      cliamItemDamageId: +payload["claimItemsDamageAreasId"],
    }),
    ...(payload["claimItemsId"] && { claimItemsId: +payload["claimItemsId"] }),
  };
};
